import { useContext } from "react";
import { ValidationResult } from "sprint-common-web";

import { AppConfigContext } from "../../../../core/configuration/Configuration";
import { useAuthorizedFetch } from "../../../../core/data-access/useAuthorizedFetch";
import { cacheRequests } from "../../../utils/cacheRequests";
import { AddSystemPlatformRequest } from "./AddSystemPlatformRequest";
import { GetOrdersAirlinesLookupResponse } from "./GetOrdersAirlinesLookupResponse";
import { GetSystemPlatformDetailsRequest } from "./GetSystemPlatformDetailsRequest";
import { GetSystemPlatformDetailsResponse } from "./GetSystemPlatformDetailsResponse";
import { GetSystemPlatformsListRequest } from "./GetSystemPlatformsListRequest";
import { GetSystemPlatformsListResponse } from "./GetSystemPlatformsListResponse";
import { RemoveSystemPlatformRequest } from "./RemoveSystemPlatformRequest";
import { UpdateBuzzIdsRequest } from "./UpdateBuzzIdsRequest";
import { UpdateShreqIdsRequest } from "./UpdateShreqIdsRequest";
import { UpdateDeliveryInfoRequest } from "./UpdateDeliveryInfoRequest";
import { UpdateEncodeConfigurationRequest } from "./UpdateEncodeConfigurationRequest";
import { UpdateSpecificationRequest } from "./UpdateSpecificationRequest";
import { ValidateSystemRequest } from "./ValidateSystemRequest";

const airlinesCache = cacheRequests<GetOrdersAirlinesLookupResponse>();

export const useOrdersAirlineApi = () => {
    const appConfigContext = useContext(AppConfigContext);
    const { httpGet, httpPut } = useAuthorizedFetch();
    const baseUrl = `${appConfigContext.ordersConfig.apiUrl}/airlines`;

    const getOrdersAirlinesLookup = async (): Promise<GetOrdersAirlinesLookupResponse> =>
        airlinesCache.getRequest({
            requestFn: () => httpGet<GetOrdersAirlinesLookupResponse>(`${baseUrl}/get-lookup`),
            key: "order-airlines-lookup",
            ttlMs: 20000
        });

    const getSystemPlatformsList = async (
        request: GetSystemPlatformsListRequest
    ): Promise<GetSystemPlatformsListResponse> => {
        return httpGet<GetSystemPlatformsListResponse>(`${baseUrl}/get-system-platforms-list`, request);
    };

    const getSystemPlatformDetails = async (
        request: GetSystemPlatformDetailsRequest
    ): Promise<GetSystemPlatformDetailsResponse> => {
        return httpGet<GetSystemPlatformDetailsResponse>(`${baseUrl}/get-system-platform-details`, request);
    };

    const addSystemPlatform = async (request: AddSystemPlatformRequest): Promise<void> => {
        return httpPut(`${baseUrl}/add-system-platform`, request);
    };

    const removeSystemPlatform = async (request: RemoveSystemPlatformRequest): Promise<void> => {
        return httpPut(`${baseUrl}/remove-system-platform`, request);
    };

    const updateSpecification = async (request: UpdateSpecificationRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-specification`, request);
    };

    const validateSystem = async (request: ValidateSystemRequest): Promise<ValidationResult> => {
        return httpGet(`${baseUrl}/validate-system`, request);
    };

    const updateEncodeConfiguration = async (request: UpdateEncodeConfigurationRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-encode-configuration`, request);
    };

    const updateDeliveryInfo = async (request: UpdateDeliveryInfoRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-delivery-info`, request);
    };

    const updateBuzzIds = async (request: UpdateBuzzIdsRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-buzz-ids`, request);
    };

    const updateShreqIds = async (request: UpdateShreqIdsRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-shreq-ids`, request);
    };

    return {
        getOrdersAirlinesLookup,
        addSystemPlatform,
        removeSystemPlatform,
        updateSpecification,
        validateSystem,
        updateEncodeConfiguration,
        updateDeliveryInfo,
        updateBuzzIds,
        updateShreqIds,
        getSystemPlatformsList,
        getSystemPlatformDetails
    };
};
